/**
 * Pointer-only install path.
 *
 * Given an {@link InstallManifest} (the Pointer Triple the Catalog hands
 * clients), this:
 *
 *   1. fetches the asset bytes directly from the upstream repo at the pinned
 *      commit (via an injectable fetcher, default {@link fetchAssetFilesFromGitHub});
 *   2. verifies EVERY per-file SHA256 against `manifest.files[].sha256`;
 *   3. verifies the composite SHA256 rollup over the sorted
 *      `<path>:<sha256>\n` lines against `manifest.sha256`;
 *   4. writes the verified bytes under `<libraryRoot>/@<publisher>/<name>/<version>`.
 *
 * Any hash mismatch is a hard failure — bytes are not written if per-file
 * verification fails, and the composite check is the second gate.
 *
 * @docLink packages/library/concepts#install
 */

import { createHash } from "node:crypto";
import { mkdir, rm, writeFile } from "node:fs/promises";
import { dirname, isAbsolute, join, resolve, sep } from "node:path";
import { parseAssetRef } from "@skaile/workspaces/core";
import type { InstallManifest } from "@skaile/workspaces/types";

/**
 * Validate a single manifest-supplied file path against path traversal.
 *
 * `manifest.files[].path` comes off the network — the attacker controls the
 * entire manifest (and therefore every hash in it), so hash verification can
 * NEVER mitigate a malicious *destination*. A path like
 * `../../../../home/user/.bashrc` or an absolute path would escape
 * `installPath` and yield an arbitrary file write. Every path is checked
 * BEFORE any fetch or write happens; the first bad path aborts the entire
 * install with a hard throw.
 *
 * A path is rejected if ANY of:
 *   - it is empty;
 *   - it is absolute (`path.isAbsolute`) or has a leading slash;
 *   - it contains a `..` segment;
 *   - `path.resolve(installPath, p)` is not `installPath` itself and does not
 *     start with `installPath + path.sep` (escapes the install dir).
 *
 * @throws An error matching `/unsafe path in manifest/` on the first bad path.
 */
function assertSafeManifestPath(installPath: string, p: string): void {
  const bad = (): never => {
    throw new Error(`unsafe path in manifest: ${p}`);
  };
  if (!p || p.length === 0) bad();
  if (isAbsolute(p) || p.startsWith("/") || p.startsWith("\\")) bad();
  const segments = p.split(/[/\\]/);
  if (segments.some((s) => s === "..")) bad();
  const resolved = resolve(installPath, p);
  if (resolved !== installPath && !resolved.startsWith(installPath + sep)) bad();
}

/**
 * Validate one component (publisher / name / version) of the attacker-controlled
 * `manifest.ref`.
 *
 * `installPath` is built by joining these components onto `libraryRoot`. A
 * component containing a path separator or a `..` / leading-dot segment would
 * let `installPath` itself escape `libraryRoot` — yielding an arbitrary file
 * write AND, via the pre-write `rm(installPath, { recursive })`, an arbitrary
 * recursive delete. The per-file `assertSafeManifestPath` guard cannot catch
 * this: it only validates each `f.path` *relative to* `installPath`, never
 * `installPath` itself. Components must be plain identifier-like strings.
 *
 * @throws An error matching `/unsafe ref/` on a bad component.
 */
function assertSafeRefComponent(kind: string, value: string): void {
  // Reject empty, leading-dot (covers `.` and `..`), path separators, and
  // ASCII control characters (NUL et al.) — fails a malformed segment fast
  // with a domain-specific error rather than a raw EINVAL deeper in.
  const hasControlChar = [...value].some((c) => c.charCodeAt(0) < 0x20);
  if (!value || value.startsWith(".") || /[/\\]/.test(value) || hasControlChar) {
    throw new Error(`unsafe ref ${kind} component in manifest: '${value}'`);
  }
}

/**
 * Longest common **directory** prefix across manifest paths, compared by whole
 * slash-delimited segments (never raw-string — so `app/` and `app-data/` never
 * collide). Each path's basename is excluded, so the result is always a
 * directory prefix and every path keeps a non-empty remainder after stripping.
 *
 * Used by flat mode to reproduce the platform pointer-installer's asset-rooting:
 * the store persists repo-root-relative paths (e.g. `skaile-platform/mcpo/xls/MCP.md`)
 * but the runner scans `.skaile/assets/<kind>/<name>/` for immediate-child files,
 * so this prefix is stripped from the WRITE path while the fetch keeps the full
 * repo path. Returns `''` when there is no common directory (a no-op strip).
 */
export function longestCommonDirPrefix(paths: readonly string[]): string {
  if (paths.length === 0) return "";
  const dirSegments = paths.map((p) => p.split("/").slice(0, -1));
  let common = dirSegments[0]!;
  for (let i = 1; i < dirSegments.length && common.length > 0; i++) {
    const segs = dirSegments[i]!;
    let n = 0;
    while (n < common.length && n < segs.length && common[n] === segs[n]) n++;
    common = common.slice(0, n);
  }
  return common.join("/");
}

/**
 * Fetcher signature — fetches `{ path → Buffer }` for the requested paths at
 * a pinned upstream commit. {@link fetchAssetFilesFromGitHub} is the default
 * production implementation; tests inject a mock.
 */
export type AssetFetcher = (opts: {
  url: string;
  commitSha: string;
  paths: string[];
}) => Promise<Record<string, Buffer>>;

/**
 * Options for {@link installFromManifest}.
 */
export interface InstallOpts {
  /** The pointer-only install manifest from the Catalog. */
  manifest: InstallManifest;
  /** Filesystem root the asset is installed under (ref-derived mode). */
  libraryRoot: string;
  /**
   * Pluggable fetcher so tests can inject mock data. Defaults to
   * {@link fetchAssetFilesFromGitHub} in production.
   */
  fetcher?: AssetFetcher;
  /**
   * Flat-layout mode. When set, install-core reproduces the platform
   * pointer-installer's behavior: it ignores `libraryRoot`/ref-derivation, does
   * NOT parse `manifest.ref` and does NOT verify the composite sha256, strips
   * the longest-common-dir prefix from each path, per-file sha256-verifies, and
   * writes the stripped paths as immediate children of `targetDir` (what the
   * runner's `.skaile/assets/<kind>/<name>/` scan requires). `installPath` is
   * `targetDir`. Absent → unchanged ref-derived `@<pub>/name/version/<path>`
   * layout with composite verify.
   */
  targetDir?: string;
}

/**
 * Result of a successful {@link installFromManifest} call.
 */
export interface InstallResult {
  /** Absolute path the asset files were written to. */
  installPath: string;
}

/**
 * Fetch, verify, and write an asset's files per its {@link InstallManifest}.
 *
 * @throws An error matching `/hash mismatch/i` if any per-file SHA256 differs.
 * @throws An error matching `/composite hash mismatch/i` if the rollup differs.
 * @throws An error if the ref is malformed or the fetcher omits a file.
 */
export async function installFromManifest(opts: InstallOpts): Promise<InstallResult> {
  const { manifest, libraryRoot } = opts;
  const fetcher =
    opts.fetcher ?? (await import("./source-fetch-github.js")).fetchAssetFilesFromGitHub;

  // Flat mode: reproduce the platform pointer-installer's asset-rooted layout.
  // No ref parse, no composite verify — the platform store may not satisfy a
  // ref-grammar or composite, and the runner only needs immediate-child files.
  if (opts.targetDir !== undefined) {
    return installFlat(manifest, opts.targetDir, fetcher);
  }

  // 1. Parse ref into install path: `skill:@x/git#1.0.0` → `<root>/@x/git/1.0.0`.
  // Done first so the C1 path-traversal guard can resolve against installPath
  // BEFORE any network fetch happens. The ref is attacker-controlled, so each
  // segment is further validated as a safe path component (H1) after parsing.
  // `parseAssetRef` is the single canonical grammar (`kind:@publisher/name#version`);
  // the legacy trailing-`@version` shape was removed in core and is rejected here.
  let parsed: ReturnType<typeof parseAssetRef>;
  try {
    parsed = parseAssetRef(manifest.ref);
  } catch (e) {
    throw new Error(`invalid ref: ${manifest.ref}: ${e instanceof Error ? e.message : String(e)}`);
  }
  const publisher = parsed.publisher;
  const name = parsed.name;
  const version = parsed.pin;
  if (!publisher) throw new Error(`invalid ref: ${manifest.ref} (missing publisher)`);
  if (!version) throw new Error(`invalid ref: ${manifest.ref} (missing version pin)`);
  assertSafeRefComponent("publisher", publisher);
  assertSafeRefComponent("name", name);
  assertSafeRefComponent("version", version);
  const root = resolve(libraryRoot);
  const installPath = resolve(join(root, `@${publisher}`, name, version));
  // Defense in depth: even with the segment guards above, assert the built
  // installPath stays strictly inside libraryRoot before any write or `rm`.
  if (installPath !== root && !installPath.startsWith(root + sep)) {
    throw new Error(`unsafe install path: ref '${manifest.ref}' resolves outside the library root`);
  }

  // 2. [C1] Validate EVERY manifest path against traversal BEFORE fetching.
  // The attacker controls the manifest (and thus all hashes) — hashes verify
  // content, never destination. Reject the whole install on the first bad path.
  for (const f of manifest.files) {
    assertSafeManifestPath(installPath, f.path);
  }

  // 3. Fetch all files at the pinned commit.
  const paths = manifest.files.map((f) => f.path);
  const bytes = await fetcher({
    url: manifest.source.url,
    commitSha: manifest.source.commitSha,
    paths,
  });

  // 4. Verify per-file SHA256 — hard fail before writing anything. The path
  // key used to read from the fetcher result is re-guarded as defense in depth.
  for (const f of manifest.files) {
    assertSafeManifestPath(installPath, f.path);
    const buf = bytes[f.path];
    if (!buf) throw new Error(`fetcher returned no bytes for ${f.path}`);
    const actual = createHash("sha256").update(buf).digest("hex");
    if (actual !== f.sha256) {
      throw new Error(`hash mismatch for ${f.path}: expected ${f.sha256}, got ${actual}`);
    }
  }

  // 5. Verify composite SHA256 over the sorted `<path>:<sha256>\n` lines.
  const sortedLines = manifest.files
    .slice()
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((e) => `${e.path}:${e.sha256}\n`)
    .join("");
  const actualComposite = createHash("sha256").update(sortedLines, "utf-8").digest("hex");
  if (actualComposite !== manifest.sha256) {
    throw new Error(`composite hash mismatch: expected ${manifest.sha256}, got ${actualComposite}`);
  }

  // 6. [M1] Write all verified files. A failed install must leave NO partial
  // directory: any prior `installPath` is removed first (clean overwrite), and
  // a mid-loop write failure removes the partially-written tree before
  // rethrowing.
  await rm(installPath, { recursive: true, force: true });
  try {
    for (const f of manifest.files) {
      const target = join(installPath, f.path);
      await mkdir(dirname(target), { recursive: true });
      await writeFile(target, bytes[f.path]!);
    }
  } catch (e) {
    await rm(installPath, { recursive: true, force: true }).catch(() => {});
    throw e;
  }

  return { installPath };
}

/**
 * Flat-layout install — the platform pointer-installer's behavior, made canonical
 * in install-core. Validates every path, fetches with the FULL repo paths, per-file
 * sha256-verifies, then strips the longest-common-dir prefix and writes each file
 * as `join(targetDir, path.slice(assetRoot.length + 1))` — immediate children of
 * `targetDir`. No ref parse, no composite check.
 */
async function installFlat(
  manifest: InstallManifest,
  targetDir: string,
  fetcher: AssetFetcher,
): Promise<InstallResult> {
  const target = resolve(targetDir);

  // Gate every path before any fetch — the attacker controls the whole manifest.
  for (const f of manifest.files) {
    assertSafeManifestPath(target, f.path);
  }

  // Fetch keys off the FULL repo path (matches the upstream tree).
  const paths = manifest.files.map((f) => f.path);
  const bytes = await fetcher({
    url: manifest.source.url,
    commitSha: manifest.source.commitSha,
    paths,
  });

  // Strip the asset-root dir prefix from the WRITE path only — the fetch above
  // still keyed off the full repo path. Stripping removes leading whole segments
  // (never the basename), so a non-empty remainder always survives.
  const assetRoot = longestCommonDirPrefix(paths);

  for (const f of manifest.files) {
    const buf = bytes[f.path];
    if (!buf) throw new Error(`fetcher returned no bytes for ${f.path}`);
    const actual = createHash("sha256").update(buf).digest("hex");
    if (actual !== f.sha256) {
      throw new Error(`hash mismatch for ${f.path}: expected ${f.sha256}, got ${actual}`);
    }
    const relPath = assetRoot ? f.path.slice(assetRoot.length + 1) : f.path;
    // Re-guard the post-strip destination — it is what we actually write.
    assertSafeManifestPath(target, relPath);
    const dest = join(target, relPath);
    await mkdir(dirname(dest), { recursive: true });
    await writeFile(dest, buf);
  }

  return { installPath: target };
}
